import { GameVars } from "../../../GameVars";
import { GameType } from "../../../Types";
import { BoardLayoutManager } from "../BoardLayoutManager";

export class HandsCounter extends Phaser.GameObjects.Container {

    private static readonly MARGIN = 16;
    private static readonly DISPLAY_SCALE = .8;
    private readonly background: Phaser.GameObjects.Image;
    private readonly divider: Phaser.GameObjects.Graphics;
    private readonly gameTypeLabel: Phaser.GameObjects.Text;
    private readonly handLabel: Phaser.GameObjects.Text;
    private readonly handNumber: Phaser.GameObjects.Text;
    private readonly handLimit: Phaser.GameObjects.Text;
    private readonly gamesLabel: Phaser.GameObjects.Text;
    private readonly northSouthLabel: Phaser.GameObjects.Text;
    private readonly northSouthValue: Phaser.GameObjects.Text;
    private readonly eastWestLabel: Phaser.GameObjects.Text;
    private readonly eastWestValue: Phaser.GameObjects.Text;
    private readonly northSouthGamesLabel: Phaser.GameObjects.Text;
    private readonly northSouthGamesValue: Phaser.GameObjects.Text;
    private readonly eastWestGamesLabel: Phaser.GameObjects.Text;
    private readonly eastWestGamesValue: Phaser.GameObjects.Text;
    private gameType?: GameType;
    private playPhaseInfoOffset = false;
    private playPhaseTweenTargetY?: number;
    private readonly onUpdate = () => this.refresh();

    constructor(scene: Phaser.Scene) {

        super(scene);

        this.background = new Phaser.GameObjects.Image(
            scene, 0, 0, "texture_atlas_1", "hands_container"
        ).setOrigin(.5);
        this.divider = new Phaser.GameObjects.Graphics(scene);
        this.gameTypeLabel = this.createLabel(-50, -40, "", 28, "#FFFFFF");
        this.handLabel = this.createLabel(-50, 0, "HAND", 24, "#FFFFFF");
        this.handNumber = this.createLabel(32, 0, "", 26, "#ffda72");
        this.handLimit = this.createLabel(58, 0, "/ 4", 24, "#FFFFFF");
        this.gamesLabel = this.createLabel(-50, 40, "GAMES", 18, "#8FC4D4");
        this.northSouthLabel = this.createLabel(0, 40, "N-S", 18, "#8FC4D4");
        this.northSouthValue = this.createLabel(0, 40, "", 22, "#ffda72");
        this.eastWestLabel = this.createLabel(0, 40, "E-W", 18, "#8FC4D4");
        this.eastWestValue = this.createLabel(0, 40, "", 22, "#FFFFFF");
        this.northSouthGamesLabel = this.createLabel(24, 40, "N-S", 18, "#8FC4D4");
        this.northSouthGamesValue = this.createLabel(64, 40, "", 22, "#ffda72");
        this.eastWestGamesLabel = this.createLabel(104, 40, "E-W", 18, "#8FC4D4");
        this.eastWestGamesValue = this.createLabel(147, 40, "", 22, "#FFFFFF");

        this.add([
            this.background, this.divider, this.gameTypeLabel, this.handLabel, this.handNumber,
            this.handLimit, this.gamesLabel, this.northSouthLabel, this.northSouthValue,
            this.eastWestLabel, this.eastWestValue, this.northSouthGamesLabel,
            this.northSouthGamesValue, this.eastWestGamesLabel, this.eastWestGamesValue
        ]);
        this.refresh();
        this.relayout();
        scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    }

    public refresh(): void {

        const gameType = GameVars.gameData?.gameType ?? GameType.CHICAGO;
        const chicago = gameType === GameType.CHICAGO;
        if (this.gameType !== gameType) {
            this.gameType = gameType;
            this.gameTypeLabel.setText(gameType.toUpperCase());
            this.gameTypeLabel.setY(chicago ? -40 : -52);
            this.handLabel.setY(chicago ? 0 : -20);
            this.handNumber.setY(chicago ? 0 : -20);
            this.handLimit.setVisible(chicago);
            this.gamesLabel.setVisible(!chicago);
            this.northSouthGamesLabel.setVisible(!chicago);
            this.northSouthGamesValue.setVisible(!chicago);
            this.eastWestGamesLabel.setVisible(!chicago);
            this.eastWestGamesValue.setVisible(!chicago);
            this.northSouthLabel.setPosition(-50, chicago ? 40 : 10);
            this.northSouthValue.setPosition(-7, chicago ? 40 : 10);
            this.eastWestLabel.setPosition(66, chicago ? 40 : 10);
            this.eastWestValue.setPosition(105, chicago ? 40 : 10);
            this.divider.clear().lineStyle(2, 0x008fce, 1);
            this.divider.lineBetween(-50, chicago ? -20 : -35, 156, chicago ? -20 : -35);
            this.divider.lineBetween(-50, chicago ? 20 : -5, 156, chicago ? 20 : -5);
            this.divider.lineBetween(54, chicago ? 30 : 0, 54, chicago ? 50 : 20);
            if (!chicago) {
                this.divider.lineBetween(-50, 25, 156, 25);
                this.divider.lineBetween(90, 30, 90, 50);
            }
        }
        const { roundNumber, northSouth, eastWest } = GameVars.matchState;
        this.updateValue(this.handNumber, roundNumber, chicago ? 24 : 100);
        this.updateValue(this.northSouthValue, northSouth.totalPoints, 51);
        this.updateValue(this.eastWestValue, eastWest.totalPoints, 51);
        this.updateValue(this.northSouthGamesValue, northSouth.gamesWon, 20);
        this.updateValue(this.eastWestGamesValue, eastWest.gamesWon, 17);
    }

    public destroy(fromScene?: boolean): void {

        if (!this.scene) {
            return;
        }
        this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
        this.scene.tweens.killTweensOf(this);
        super.destroy(fromScene);
    }

    public startPlayLayoutTransition(): void {

        if (this.playPhaseInfoOffset) {
            return;
        }
        this.playPhaseInfoOffset = true;
        if (!GameVars.isLandscape) {
            const targetY = GameVars.gameHeight * BoardLayoutManager.PORTRAIT_INFO_Y_RATIO
                + BoardLayoutManager.PORTRAIT_PLAY_INFO_OFFSET;
            this.playPhaseTweenTargetY = targetY;
            this.scene.tweens.add({
                targets: this,
                y: targetY,
                duration: BoardLayoutManager.PORTRAIT_PLAY_INFO_DURATION,
                ease: "Cubic.Out",
                onComplete: () => { this.playPhaseTweenTargetY = undefined; }
            });
        }
    }

    private updateValue(label: Phaser.GameObjects.Text, value: number, maxWidth: number): void {

        const text = `${value}`;
        if (label.text !== text) {
            label.setText(text);
        }
        label.setScale(Math.min(1, maxWidth / label.width), 1);
    }

    public relayout(): void {

        const scale = Math.min(
            HandsCounter.DISPLAY_SCALE,
            (GameVars.gameWidth - HandsCounter.MARGIN * 2) / this.background.width
        );
        const x = HandsCounter.MARGIN + this.background.width * scale / 2;
        const y = GameVars.isLandscape ? GameVars.gameHeight - HandsCounter.MARGIN * GameVars.scaleY
                - this.background.height * scale * GameVars.scaleY / 2
                : GameVars.gameHeight * BoardLayoutManager.PORTRAIT_INFO_Y_RATIO
                    + (this.playPhaseInfoOffset ? BoardLayoutManager.PORTRAIT_PLAY_INFO_OFFSET : 0);
        if (this.playPhaseTweenTargetY === y) {
            this.setX(x);
        } else {
            this.scene.tweens.killTweensOf(this);
            this.playPhaseTweenTargetY = undefined;
            this.setPosition(x, y);
        }
        this.setScale(scale, scale * GameVars.scaleY);
    }

    private createLabel(
        x: number, y: number, text: string, fontSize: number, color: string
    ): Phaser.GameObjects.Text {

        return new Phaser.GameObjects.Text(this.scene, x, y, text, {
            fontFamily: "Baloo2-Bold",
            fontSize: `${fontSize}px`,
            color,
            stroke: "#001124",
            strokeThickness: 2
        }).setOrigin(0, .5);
    }
}
